<template>
  <div>
  <v-dialog
    v-model="dialog"
    persistent
    :max-width="$vuetify.breakpoint.mobile ? '98vw' : '960'"
    content-class="dds-verify-payment-dialog"
  >
    <v-card style="position: relative;">
      <v-toolbar color="primary" elevation="0">
        <v-toolbar-title class="white--text">{{ $t('dueDiligence.verifyPaymentReceipt') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="white" icon @click="closeModal" :disabled="submitting">
          <v-icon style="color:white !important">mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-overlay :value="submitting" absolute opacity="0.55" z-index="6">
        <div class="d-flex flex-column align-center white--text">
          <v-progress-circular indeterminate color="white" size="48" class="mb-3"></v-progress-circular>
          <div class="text-center">{{ submittingMessage }}</div>
        </div>
      </v-overlay>

      <v-card-text class="pa-0 verify-scroll">
        <div class="verify-document">
          <div class="verify-top">
            <div>
              <div class="verify-brand">{{ issuerName }}</div>
              <div class="verify-subtitle">{{ $t('dueDiligence.verifyPaymentSubtitle') }}</div>
            </div>
            <!-- <div class="verify-status-chip" :class="statusClass">
              {{ paymentStatusLabel }}
            </div> -->
          </div>

          <!-- Invoice / DDS summary -->
          <section class="verify-section">
            <h3 class="section-title">{{ $t('dueDiligence.invoiceHeader') }}</h3>
            <div class="section-grid">
              <div class="field">
                <span class="label">{{ $t('dueDiligence.invoiceNumber') }}</span>
                <span class="value">{{ invoiceNumber }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.ddsReferenceNo') }}</span>
                <span class="value">{{ ddsReference }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.invoiceDate') }}</span>
                <span class="value">{{ invoiceDate }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.paymentStatus') }}</span>
                <span class="value">{{ paymentStatusLabel }}</span>
              </div>
            </div>
          </section>

          <!-- Payer -->
          <section class="verify-section">
            <h3 class="section-title">{{ $t('dueDiligence.payerInformation') }}</h3>
            <div class="section-grid">
              <div class="field">
                <span class="label">{{ $t('dueDiligence.paymentPayer') }}</span>
                <span class="value">{{ payerDisplay }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.exporterName') }}</span>
                <span class="value">{{ payer.name }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.exporterCompanyId') }}</span>
                <span class="value">{{ payer.companyId }}</span>
              </div>
              <div class="field">
                <span class="label">{{ $t('dueDiligence.exporterContact') }}</span>
                <span class="value">{{ payer.contact }}</span>
              </div>
            </div>
          </section>

          <!-- Product / pricing -->
          <section class="verify-section">
            <h3 class="section-title">{{ $t('dueDiligence.ddsProductInformation') }}</h3>
            <table class="verify-table">
              <thead>
                <tr>
                  <th>{{ $t('deforestation.product') }}</th>
                  <th class="text-right">{{ $t('dueDiligence.quantityNetMass') }}</th>
                  <th class="text-right">{{ $t('dueDiligence.unit') }}</th>
                  <th class="text-right">{{ $t('dueDiligence.pricePerTon') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ productName }}</td>
                  <td class="text-right">{{ formattedNetMass }}</td>
                  <td class="text-right">{{ $t('dueDiligence.unitTon') }}</td>
                  <td class="text-right">{{ formattedPricePerTon }}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <!-- Amount due -->
          <section class="verify-section highlight">
            <h3 class="section-title">{{ $t('dueDiligence.paymentInformation') }}</h3>
            <div class="total-row">
              <span>{{ $t('dueDiligence.paymentAmountDue') }}</span>
              <strong>{{ formattedAmount }}</strong>
            </div>
          </section>

          <!-- PTSI authorized signatory (fixed) + acting reviewer -->
          <section class="verify-section signature-section" v-if="!showRejectionReason">
            <h3 class="section-title">{{ $t('dueDiligence.ptsiAuthorizedSignatoryTitle') }}</h3>
            <p class="section-hint">{{ $t('dueDiligence.ptsiVerifyOnBehalfHint') }}</p>

            <div class="signatory-card">
              <div class="section-grid">
                <div class="field">
                  <span class="label">{{ $t('dueDiligence.signatoryName') }}</span>
                  <span class="value">{{ ptsiSignatory.name }}</span>
                </div>
                <div class="field">
                  <span class="label">{{ $t('dueDiligence.signatoryPosition') }}</span>
                  <span class="value">{{ ptsiSignatory.position }}</span>
                </div>
                <div class="field full">
                  <span class="label">{{ $t('dueDiligence.signatoryCompany') }}</span>
                  <span class="value">{{ ptsiSignatory.company }}</span>
                </div>
                <div class="field full digital-signature-field">
                  <span class="label">{{ $t('dueDiligence.digitalSignature') }}</span>
                  <div v-if="loadingExistingSignature" class="signature-loading-inline">
                    <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
                  </div>
                  <div v-else-if="signaturePreviewUrl" class="signatory-signature-preview">
                    <img :src="signaturePreviewUrl" :alt="$t('dueDiligence.approvalSignaturePreview')" />
                    <v-btn
                      small
                      text
                      color="primary"
                      class="mt-1"
                      @click="openSignatureModal = true"
                    >
                      <v-icon left small>mdi-draw</v-icon>
                      {{ $t('dueDiligence.changeSignature') }}
                    </v-btn>
                  </div>
                  <div v-else class="digital-signature-empty">
                    <span class="value pending-signature">{{ $t('dueDiligence.digitalSignatureLater') }}</span>
                    <v-btn
                      color="primary"
                      depressed
                      small
                      class="mt-2"
                      @click="openSignatureModal = true"
                    >
                      <v-icon :style="{ color: 'white !important' }" left small>mdi-draw</v-icon>
                      {{ $t('dueDiligence.addSignature') }}
                    </v-btn>
                  </div>
                  <p v-if="signatureError" class="signature-error mt-2 mb-0">{{ signatureError }}</p>
                </div>
              </div>
            </div>

            <div class="reviewer-card">
              <h4 class="reviewer-title">{{ $t('dueDiligence.verifyingAsReviewer') }}</h4>
              <div class="section-grid">
                <div class="field">
                  <span class="label">{{ $t('dueDiligence.reviewerName') }}</span>
                  <span class="value">{{ reviewerName }}</span>
                </div>
                <div class="field">
                  <span class="label">{{ $t('dueDiligence.reviewerEmail') }}</span>
                  <span class="value">{{ reviewerEmail }}</span>
                </div>
              </div>
              <p class="section-hint reviewer-signature-hint mb-0">{{ $t('dueDiligence.ptsiReviewerSignatureHint') }}</p>
            </div>
          </section>

          <!-- Bank details (for reviewer context) -->
          <section class="verify-section" v-if="hasBankDetails">
            <h3 class="section-title">{{ $t('dueDiligence.bankDetails') }}</h3>
            <div class="bank-box">
              <div><strong>{{ $t('dueDiligence.bankName') }}:</strong> {{ bankDetails.bankName || '-' }}</div>
              <div><strong>{{ $t('dueDiligence.accountName') }}:</strong> {{ bankDetails.accountName || '-' }}</div>
              <div><strong>{{ $t('dueDiligence.accountNumber') }}:</strong> {{ bankDetails.accountNumber || '-' }}</div>
            </div>
          </section>

          <!-- Submitted proof + embedded receipt -->
          <section class="verify-section proof-section">
            <h3 class="section-title">{{ $t('dueDiligence.submittedPaymentProof') }}</h3>
            <template v-if="hasSubmittedReceipt">
              <div class="section-grid mb-3">
                <div class="field">
                  <span class="label">{{ $t('dueDiligence.receiptSubmittedBy') }}</span>
                  <span class="value">{{ submittedByLabel }}</span>
                </div>
                <div class="field">
                  <span class="label">{{ $t('dueDiligence.receiptSubmittedAt') }}</span>
                  <span class="value">{{ submittedAtLabel }}</span>
                </div>
                <div class="field full" v-if="payment && payment.transaction_note">
                  <span class="label">{{ $t('dueDiligence.paymentProofNote') }}</span>
                  <span class="value">{{ payment.transaction_note }}</span>
                </div>
              </div>

              <div class="receipt-preview-header">
                <span class="receipt-preview-title">{{ $t('dueDiligence.receiptPreview') }}</span>
                <v-btn
                  small
                  text
                  color="primary"
                  :loading="loadingReceipt"
                  :disabled="!receiptUrl"
                  @click="openReceiptInNewTab"
                >
                  <v-icon left small>mdi-open-in-new</v-icon>
                  {{ $t('dueDiligence.openSubmittedReceipt') }}
                </v-btn>
              </div>

              <div class="receipt-preview-frame" v-if="loadingReceipt">
                <div class="receipt-loading">
                  <v-progress-circular indeterminate color="primary" size="36"></v-progress-circular>
                  <span>{{ $t('dueDiligence.loadingReceipt') }}</span>
                </div>
              </div>
              <div class="receipt-preview-frame" v-else-if="receiptLoadError">
                <div class="receipt-empty">
                  <v-icon large color="grey">mdi-file-alert-outline</v-icon>
                  <p>{{ $t('dueDiligence.receiptPreviewUnavailable') }}</p>
                  <v-btn small outlined color="primary" @click="loadReceiptPreview">
                    {{ $t('dueDiligence.receiptPreviewRetry') }}
                  </v-btn>
                </div>
              </div>
              <div class="receipt-preview-frame" v-else-if="receiptUrl && isImageReceipt">
                <img :src="receiptUrl" :alt="$t('dueDiligence.paymentReceipt')" class="receipt-image" />
              </div>
              <div class="receipt-preview-frame" v-else-if="receiptUrl && isPdfReceipt">
                <iframe :src="receiptUrl" class="receipt-iframe" title="payment-receipt"></iframe>
              </div>
              <div class="receipt-preview-frame" v-else-if="receiptUrl">
                <div class="receipt-empty">
                  <v-icon large color="primary">mdi-file-document-outline</v-icon>
                  <p>{{ $t('dueDiligence.receiptPreviewUnsupported') }}</p>
                  <v-btn small depressed color="primary" @click="openReceiptInNewTab">
                    {{ $t('dueDiligence.openSubmittedReceipt') }}
                  </v-btn>
                </div>
              </div>
            </template>
            <p v-else class="proof-empty">{{ $t('dueDiligence.receiptNotSubmittedYet') }}</p>
          </section>

          <!-- Rejection reason -->
          <section class="verify-section" v-if="showRejectionReason">
            <h3 class="section-title">{{ $t('dueDiligence.paymentRejectionReasonLabel') }}</h3>
            <v-textarea
              v-model="rejectionReason"
              :label="$t('dueDiligence.paymentRejectionReasonLabel')"
              outlined
              rows="3"
              hide-details="auto"
              :rules="[rules.requiredIfRejecting]"
            ></v-textarea>
          </section>
        </div>
      </v-card-text>

      <v-card-actions class="px-6 pb-4 verify-actions">
        <v-spacer></v-spacer>
        <v-btn outlined color="primary" @click="closeModal" :disabled="submitting">
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          depressed
          color="error"
          @click="reject"
          :loading="submitting && submittingAction === 'reject'"
          :disabled="submitting || (showRejectionReason && !rejectionReason.trim())"
        >
          {{ $t('dueDiligence.rejectPayment') }}
        </v-btn>
        <v-btn
          depressed
          color="primary"
          @click="approve"
          v-if="!showRejectionReason"
          :loading="submitting && submittingAction === 'approve'"
          :disabled="submitting"
        >
          {{ $t('dueDiligence.approvePayment') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import { normalizeDdsStatus, showDdsPaymentBankDetails, resolveDdsExporterPayerFields } from '@/utils';
import S3UploadService from '@/_services/S3UploadService';

const PTSI_AUTHORIZED_SIGNATORY = {
  name: 'Rosikh Adicandra',
  position: 'VP BDSNE',
  company: 'PT Surveyor Indonesia',
};

export default {
  name: 'VerifyPaymentModal',
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    payment: {
      type: Object,
      default: null,
    },
    item: {
      type: Object,
      default: null,
    },
    payerLabel: {
      type: String,
      default: '-',
    },
    payerInfo: {
      type: Object,
      default: () => ({
        name: '-',
        companyId: '-',
        contact: '-',
      }),
    },
    bankDetails: {
      type: Object,
      default: () => ({
        bankName: '',
        accountName: '',
        accountNumber: '',
      }),
    },
    issuerName: {
      type: String,
      default: 'PT Surveyor Indonesia',
    },
  },
  data() {
    return {
      showRejectionReason: false,
      rejectionReason: '',
      loadingReceipt: false,
      receiptUrl: null,
      receiptLoadError: false,
      submitting: false,
      submittingAction: null,
      rules: {
        requiredIfRejecting: (value) =>
          !!value?.trim() || this.$t('dueDiligence.paymentRejectionReasonRequired'),
      },
    };
  },
  computed: {
    ptsiSignatory() {
      return PTSI_AUTHORIZED_SIGNATORY;
    },
    currentUser() {
      return this.$store.getters.getUser || {};
    },
    reviewerName() {
      const user = this.currentUser;
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
      return name || user.name || '-';
    },
    reviewerEmail() {
      return this.currentUser.email || this.currentUser.unverifiedEmail || '-';
    },
    currency() {
      return this.payment?.currency || 'IDR';
    },
    formattedAmount() {
      if (!this.payment || this.payment.amount == null) return '-';
      return `${this.currency} ${Number(this.payment.amount).toLocaleString()}`.trim();
    },
    invoiceNumber() {
      if (this.payment?.invoice_number) return this.payment.invoice_number;
      if (this.item?.id && this.payment?.id) return `INV-${this.item.id}-${this.payment.id}`;
      if (this.item?.id) return `INV-${this.item.id}`;
      return '-';
    },
    ddsReference() {
      const eudr = this.item?.EUDRReferenceNumber;
      const internal = this.item?.internalReferenceNumber;
      if (eudr && internal) return `${eudr} / ${internal}`;
      return eudr || internal || '-';
    },
    invoiceDate() {
      const raw =
        this.payment?.invoice_generated_at ||
        this.payment?.updatedAt ||
        this.payment?.createdAt ||
        this.item?.updatedAt;
      if (!raw) return '-';
      return moment(raw).format('DD MMM YYYY');
    },
    paymentStatus() {
      return normalizeDdsStatus(this.payment?.status);
    },
    paymentStatusLabel() {
      const map = {
        pending: this.$t('dueDiligence.paymentDue'),
        invoice_generated: this.$t('dueDiligence.invoiceGeneratedStatus'),
        reported: this.$t('dueDiligence.paymentPendingVerification'),
        verified: this.$t('dueDiligence.paymentVerified'),
        rejected: this.$t('dueDiligence.paymentRejected'),
      };
      return map[this.paymentStatus] || this.payment?.status || '-';
    },
    statusClass() {
      return {
        'status-due': ['pending', 'invoice_generated'].includes(this.paymentStatus),
        'status-pending': this.paymentStatus === 'reported',
        'status-paid': this.paymentStatus === 'verified',
        'status-rejected': this.paymentStatus === 'rejected',
      };
    },
    payerDisplay() {
      return this.payerLabel || '-';
    },
    responsibleExporter() {
      const exporters = this.item?.ddsReportExporters || [];
      if (!exporters.length) return null;
      const payerCfId = this.payment?.payer_cf_id;
      const byPayer = exporters.find(
        (exporter) => payerCfId && Number(exporter.exporter_cf_id) === Number(payerCfId)
      );
      if (byPayer) return byPayer;
      return (
        exporters.find(
          (exporter) => normalizeDdsStatus(exporter.payment_response_status) === 'accepted'
        ) || null
      );
    },
    payer() {
      const exporter = this.responsibleExporter;
      const resolved = resolveDdsExporterPayerFields(exporter, this.item?.companyID);
      const info = this.payerInfo || {};

      return {
        name: (info.name && info.name !== '-') ? info.name : resolved.name,
        companyId: (info.companyId && info.companyId !== '-') ? info.companyId : resolved.companyId,
        contact: (info.contact && info.contact !== '-') ? info.contact : resolved.contact,
      };
    },
    productName() {
      const detail = this.item?.product_detail;
      if (!detail) return this.item?.product || '-';
      return detail.code ? `${detail.code} - ${detail.name}` : detail.name;
    },
    netMassTons() {
      if (this.payment?.net_mass_tons_snapshot != null) {
        return Number(this.payment.net_mass_tons_snapshot);
      }
      const kg = this.item?.productNetMass;
      if (kg == null || isNaN(kg)) return null;
      return Number(kg) / 1000;
    },
    formattedNetMass() {
      if (this.netMassTons == null) return '-';
      return this.netMassTons.toLocaleString(undefined, { maximumFractionDigits: 4 });
    },
    pricePerTon() {
      if (this.payment?.price_per_ton_snapshot != null) {
        return Number(this.payment.price_per_ton_snapshot);
      }
      if (this.payment?.amount != null && this.netMassTons) {
        return Number(this.payment.amount) / this.netMassTons;
      }
      return null;
    },
    formattedPricePerTon() {
      if (this.pricePerTon == null) return '-';
      return `${this.currency} ${this.pricePerTon.toLocaleString()}`.trim();
    },
    hasBankDetails() {
      if (!showDdsPaymentBankDetails()) return false;
      return !!(
        this.bankDetails?.bankName ||
        this.bankDetails?.accountName ||
        this.bankDetails?.accountNumber
      );
    },
    hasSubmittedReceipt() {
      return !!this.payment?.receipt_s3_key;
    },
    submittingMessage() {
      if (this.submittingAction === 'reject') {
        return this.$t('dueDiligence.rejectingPayment');
      }
      return this.$t('dueDiligence.approvingPayment');
    },
    receiptKeyLower() {
      return String(this.payment?.receipt_s3_key || '').toLowerCase();
    },
    isImageReceipt() {
      return /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(this.receiptKeyLower);
    },
    isPdfReceipt() {
      return /\.pdf(\?|$)/i.test(this.receiptKeyLower);
    },
    submittedByLabel() {
      if (!this.hasSubmittedReceipt) return '-';
      if (this.payer.contact && this.payer.contact !== '-') return this.payer.contact;
      if (this.payer.name && this.payer.name !== '-') return this.payer.name;
      return this.payerLabel || '-';
    },
    submittedAtLabel() {
      const raw = this.payment?.reported_at;
      if (!raw) return '-';
      return moment(raw).format('DD MMM YYYY, HH:mm');
    },
  },
  methods: {
    closeModal() {
      if (this.submitting) return;
      this.resetForm();
      this.$emit('close');
    },
    resetForm() {
      this.showRejectionReason = false;
      this.rejectionReason = '';
      this.receiptUrl = null;
      this.receiptLoadError = false;
      this.loadingReceipt = false;
      this.submitting = false;
      this.submittingAction = null;
    },
    async loadReceiptPreview() {
      if (!this.payment?.receipt_s3_key) {
        this.receiptUrl = null;
        this.receiptLoadError = false;
        return;
      }
      this.loadingReceipt = true;
      this.receiptLoadError = false;
      try {
        this.receiptUrl = await S3UploadService.getFileUrl(this.payment.receipt_s3_key);
      } catch (error) {
        console.error('Error loading payment receipt preview:', error);
        this.receiptUrl = null;
        this.receiptLoadError = true;
      } finally {
        this.loadingReceipt = false;
      }
    },
    openReceiptInNewTab() {
      if (!this.receiptUrl) return;
      window.open(this.receiptUrl, '_blank');
    },
    approve() {
      if (this.submitting) return;
      this.submitting = true;
      this.submittingAction = 'approve';
      this.$emit('confirm', {
        approved: true,
        done: (error) => {
          this.submitting = false;
          this.submittingAction = null;
          if (!error) {
            this.closeModal();
            return;
          }
          this.$toast?.error?.(error?.message || this.$t('somethingWentWrong'));
        },
      });
    },
    reject() {
      if (!this.showRejectionReason) {
        this.showRejectionReason = true;
        return;
      }
      if (!this.rejectionReason.trim() || this.submitting) return;
      this.submitting = true;
      this.submittingAction = 'reject';
      this.$emit('confirm', {
        approved: false,
        rejectionReason: this.rejectionReason.trim(),
        done: (error) => {
          this.submitting = false;
          this.submittingAction = null;
          if (!error) {
            this.closeModal();
          }
        },
      });
    },
  },
  watch: {
    dialog: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.resetForm();
          this.$nextTick(() => {
            this.loadReceiptPreview();
          });
        } else {
          this.resetForm();
        }
      },
    },
    'payment.receipt_s3_key'(newKey, oldKey) {
      if (this.dialog && newKey && newKey !== oldKey) {
        this.loadReceiptPreview();
      }
    },
  },
};
</script>

<style scoped>
.verify-scroll {
  max-height: 75vh;
  overflow-y: auto;
  background: #fff;
}

.verify-document {
  background: #fff;
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 24px 28px;
  color: #1a1a1a;
  box-sizing: border-box;
}

.verify-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  border-bottom: 2px solid #0d47a1;
  padding-bottom: 16px;
  gap: 16px;
}

.verify-brand {
  font-size: 22px;
  font-weight: 700;
  color: #0d47a1;
}

.verify-subtitle {
  font-size: 13px;
  color: #555;
  margin-top: 4px;
}

.verify-status-chip {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.status-due {
  background: #fff3e0;
  color: #e65100;
}

.status-pending {
  background: #e3f2fd;
  color: #1565c0;
}

.status-paid {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-rejected {
  background: #ffebee;
  color: #c62828;
}

.verify-section {
  margin-bottom: 18px;
}

.section-title {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #0d47a1;
  margin: 0 0 10px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 6px;
}

.section-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 20px;
}

.field {
  display: flex;
  flex-direction: column;
}

.field.full {
  grid-column: 1 / -1;
}

.label {
  font-size: 11px;
  color: #666;
  margin-bottom: 2px;
}

.value {
  font-size: 14px;
  font-weight: 600;
}

.verify-table {
  width: 100%;
  border-collapse: collapse;
}

.verify-table th,
.verify-table td {
  border: 1px solid #ddd;
  padding: 8px 10px;
  font-size: 13px;
}

.verify-table th {
  background: #f5f7fa;
  text-align: left;
}

.text-right {
  text-align: right;
}

.highlight {
  background: #f8fafc;
  padding: 12px;
  border-radius: 6px;
}

.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
}

.bank-box {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  line-height: 1.7;
  font-size: 13px;
}

.proof-section {
  background: #f3f8ff;
  border: 1px solid #d6e4f5;
  border-radius: 8px;
  padding: 14px;
}

.proof-empty {
  font-size: 13px;
  color: #78909c;
  margin: 0;
  font-style: italic;
}

.receipt-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 8px;
}

.receipt-preview-title {
  font-size: 12px;
  font-weight: 600;
  color: #1565c0;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.receipt-preview-frame {
  background: #fff;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  min-height: 280px;
  max-height: 420px;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.receipt-image {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
}

.receipt-iframe {
  width: 100%;
  height: 400px;
  border: 0;
  background: #fff;
}

.receipt-loading,
.receipt-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 16px;
  text-align: center;
  color: #607d8b;
  font-size: 13px;
}

.receipt-empty p {
  margin: 0;
}


.section-hint {
  font-size: 13px;
  color: #666;
  margin: 0 0 8px;
  line-height: 1.45;
}


.signatory-card {
  background: #fff;
  border: 1px solid #dce8f5;
  border-radius: 6px;
  padding: 12px 14px;
  margin-top: 8px;
}

.reviewer-card {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed #c5d4f0;
}

.reviewer-title {
  font-size: 13px;
  font-weight: 600;
  color: #184980;
  margin: 0 0 10px;
}

.reviewer-signature-hint {
  margin-top: 12px;
}

.digital-signature-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.digital-signature-empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.signatory-signature-preview {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 12px;
  border: 1px dashed #90a4ae;
  border-radius: 6px;
  background: #fff;
  max-width: 280px;
}

.signatory-signature-preview img {
  max-height: 100px;
  max-width: 100%;
  object-fit: contain;
}

.signature-loading-inline {
  min-height: 48px;
  display: flex;
  align-items: center;
}

.pending-signature {
  color: #64748b;
  font-style: italic;
}










.approve-hint {
  flex: 1 1 100%;
  color: #e65100;
  font-size: 13px;
}

.verify-actions {
  flex-wrap: wrap;
  gap: 8px;
}

@media (max-width: 600px) {
  .verify-document {
    padding: 16px;
  }

  .section-grid {
    grid-template-columns: 1fr;
  }

  .verify-top {
    flex-direction: column;
  }

  .receipt-preview-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .receipt-iframe {
    height: 280px;
  }
}
</style>
